/* eslint-disable max-len */
import React, {
  useEffect, useState, useMemo, useCallback,
} from 'react'
import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import { useFormik } from 'formik'
import { useHistory, useLocation } from 'react-router-dom'

import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import makeStyles from '@material-ui/core/styles/makeStyles'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'

import Autocomplete from '@material-ui/lab/Autocomplete'
import SearchIcon from '@material-ui/icons/Search'

import { HELP_LINKS } from '../../constants/HelpLinks'
import { ButtonGradient, HelpLink } from '../../shared/components'
import DropdownSelectOne from '../../shared/components/DropdownSelectOne/DropdownSelectOne'

import useProductQuotationClient from '../../clients/ProductClient/useProductQuotationClient'
import useUserClient from '../../clients/UserClient/useUserClient'
import useQuotationClient from '../../clients/QuotationClient/useQuotationClient'
import useSecurity from '../../security/useSecurity'
import useTomadorSelection from '../../shared/hooks/useTomadorSelection'
import AutomaticTooltip from './quotations/AutomaticTooltip'

import { PRODUCT_JUDICIAL_GUARANTEE_APPEAL, QUOTE_URL_EXECUTIVE_REQUEST, ROUTES_EXECUTIVE_REQUEST } from '../../constants'
import { ReactComponent as Automatic } from '../../assets/img/icons/ico-automatico.svg'

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiCollapse-root': {
      borderRadius: 14,
    },
  },
  header: {
    height: 120,
    padding: 40,
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    padding: theme.spacing(4, 5),
  },
  labelInfo: {
    color: theme.palette.text.tertiary,
  },
  buttonsStep: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& .MuiBox-root': {
      minWidth: 190,
    },
  },
  label: {
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
  },
  textField: {
    '& .MuiTextField-root': {
      marginTop: 20,
    },
  },
  radioGroup: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: 30,
  },
  radioLabel: {
    color: theme.palette.text.primary,
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
    },
  },
}))

const DEFAULT_QUOTATION = {
  brokerProduct: '',
  brokerModality: '',
  performanceModality: '',
  tomador: '',
}

const APPEAL_TYPE_PUBLIC = 'Public'
const FIANCA_LOCATICIA_PATH = 'fianca-locaticia'
const FIANCA_LOCATICIA_MODALITY_PF = 1
const ENGINEERING_RISK_PATH = 'risco-engenharia'

const CategoryProduct = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()

  const productQuotationClient = useProductQuotationClient()
  const userClient = useUserClient()
  const quotationClient = useQuotationClient()
  const {
    getEmail,
  } = useSecurity()

  const [products, setProducts] = useState([''])
  const [modalities, setModalities] = useState([''])
  const [selectedProduct, setSelectedProduct] = useState('')
  const [selectedModality, setSelectedModality] = useState('')
  const [showPerformanceModality, setShowPerformanceModality] = useState(false)
  const [performanceModality, setPerformanceModality] = useState('')
  const [aiReturnRoute, setAiReturnRoute] = useState(false)
  const [tomadorSelected, setTomadorSelected] = useState('')
  const [multiQuoteProducts, setMultiQuoteProducts] = useState([])

  const getTokenUrl = location?.pathname?.split(':')
  const routeSolicitation = getTokenUrl[0]
  const tokenParams = getTokenUrl[1]
  const isExecutiveRequest = ROUTES_EXECUTIVE_REQUEST.includes(routeSolicitation)

  const isAIQuotation = location?.state?.isAIQuotation || false
  const [aiFieldsInitialized, setAiFieldsInitialized] = useState(false)

  const isFiancaLocaticia = selectedProduct?.path === FIANCA_LOCATICIA_PATH
  const isEngineeringRisk = selectedProduct?.path === ENGINEERING_RISK_PATH

  const documentTypeSize = useMemo(() => {
    if (!isFiancaLocaticia || !selectedModality) return undefined
    return selectedModality?.value === FIANCA_LOCATICIA_MODALITY_PF ? 11 : 14
  }, [isFiancaLocaticia, selectedModality])

  const shouldLoadTomador = isFiancaLocaticia
    ? !!selectedModality
    : !!selectedProduct

  const {
    hasNoneClient,
    hasUniqueClient,
    policiesHolder,
    policyHolderOptions,
    findByText,
    findFirst,
  } = useTomadorSelection({ documentTypeSize, enabled: shouldLoadTomador })

  const tomadorLabel = useMemo(() => {
    if (isFiancaLocaticia) return 'Garantido / Locatário'
    if (isEngineeringRisk) return 'Proponente'
    return 'Tomador'
  }, [isFiancaLocaticia, isEngineeringRisk])

  // --------------------------------------------------------------------- teste multi remover depois
  const getBasePrefix = useCallback(() => {
    // Solicitação executivo segue sempre o fluxo normal, sem multicálculo
    if (isExecutiveRequest) {
      return 'cotacao'
    }
    if (quotationClient().isModalityMultiQuote(selectedModality?.path, multiQuoteProducts)) {
      return 'cotacao/multicalculo'
    }
    return 'cotacao'
  }, [isExecutiveRequest, quotationClient, selectedModality, multiQuoteProducts])

  // ---------------------------------------------------------------------

  const checkIfHasAutomaticEmission = (product, modality) => {
    if (!product || !modality) return false
    const productPath = product.path || product
    const modalityText = modality.text || modality

    if (productPath === 'garantia') {
      return ['Licitante (BID)', 'Performance (Construção, Fornecimento ou Prestação de Serviços)'].includes(modalityText)
    }

    if (product.value === PRODUCT_JUDICIAL_GUARANTEE_APPEAL) {
      return ['Judicial Depósito Recursal', 'Judicial Trabalhista', 'Judicial Cível'].includes(modalityText)
    }
    return false
  }

  useEffect(() => {
    if (isAIQuotation) {
      setAiReturnRoute(true)
      setProducts(productQuotationClient().getAIProducts().sort())
    } else if (QUOTE_URL_EXECUTIVE_REQUEST === routeSolicitation) {
      setAiReturnRoute(false)
      setProducts(productQuotationClient().getAutomaticProductsForExecutiveRequest().sort())
    } else {
      setAiReturnRoute(true)
      setProducts(productQuotationClient().getProducts().sort())
    }
  }, [routeSolicitation, productQuotationClient, isAIQuotation, getEmail, userClient])

  const modalitiesIsEmpty = () => modalities.filter((m) => m).length === 0

  const withTomadorState = useCallback((extra = {}) => {
    const tomadorFound = findByText(tomadorSelected)
    return {
      ...extra,
      tomadorName: tomadorFound?.text ?? tomadorSelected ?? '',
      tomadorDocument: tomadorFound?.id ?? '',
    }
  }, [findByText, tomadorSelected])

  const handlePriceQuotation = () => {
    const basePrefix = getBasePrefix()

    let path = QUOTE_URL_EXECUTIVE_REQUEST === routeSolicitation
      ? `${QUOTE_URL_EXECUTIVE_REQUEST}${selectedProduct?.path}/:${tokenParams}`
      : `${basePrefix}/${selectedProduct?.path}`

    if (selectedModality?.path && selectedModality.path !== '') {
      if (QUOTE_URL_EXECUTIVE_REQUEST === routeSolicitation) {
        path = `${QUOTE_URL_EXECUTIVE_REQUEST}${selectedProduct?.path}/${selectedModality.path}/:${tokenParams}`
      } else {
        path += `/${selectedModality.path}`
      }
    }

    const isPerformancePublic = selectedProduct?.path === 'garantia'
    && selectedModality?.path === 'performance'
    && performanceModality === 'Construção, Fornecimento ou Prestação de serviço – Pública'

    const shouldUploadFirst = (isPerformancePublic) && aiReturnRoute

    if (shouldUploadFirst) {
      // const nextRoute = `/cotacao/${selectedProduct?.path}/${selectedModality?.path}`
      // history.push('/cotacao/upload', withTomadorState({
      const nextRoute = `/${basePrefix}/${selectedProduct?.path}/${selectedModality?.path}` // multi
      history.push(`/${basePrefix}/upload`, withTomadorState({ // multi
        productType: selectedProduct?.value,
        modalityType: selectedModality?.value,
        productPath: selectedProduct?.path,
        modalityPath: selectedModality?.path,
        performanceModality,
        redirectToAiRoute: true,
        nextRoute,
        basePrefix,
      }))
      return
    }

    if (selectedProduct?.path === 'garantia' && selectedModality?.path === 'performance') {
      history.push(path, withTomadorState({ performanceModality, redirectToAiRoute: false }))
      return
    }

    if (selectedProduct?.value === PRODUCT_JUDICIAL_GUARANTEE_APPEAL) {
      const typeOtherModality = productQuotationClient().getAllTypeOtherModality().find(
        ({ value }) => value === APPEAL_TYPE_PUBLIC,
      )

      const appealType = typeOtherModality?.modalities?.find(
        ({ id }) => id === selectedModality?.value,
      )

      if (!isEmpty(appealType)) {
        history.push(QUOTE_URL_EXECUTIVE_REQUEST === routeSolicitation
          ? `${QUOTE_URL_EXECUTIVE_REQUEST}${selectedModality?.path}/:${tokenParams}`
          // : `cotacao/${selectedModality?.path}`, withTomadorState({
          : `${basePrefix}/${selectedModality?.path}`, withTomadorState({ // multi
          guaranteeOtherJudicial: {
            modality: appealType?.value,
            modalityType: typeOtherModality?.value,
          },
          appealTypeId: appealType?.id,
        }))
        return
      }
    }

    if (selectedProduct?.path === 'fianca-locaticia' && QUOTE_URL_EXECUTIVE_REQUEST !== routeSolicitation) {
      history.push(path, withTomadorState({
        modalityLeaseBail: selectedModality?.value,
      }))
      return
    }

    history.push(path, withTomadorState())
  }

  const formik = useFormik({
    initialValues: { ...DEFAULT_QUOTATION },
    validationSchema: Yup.object({
      brokerProduct: Yup.string().required(),
      brokerModality: Yup.string().when('brokerProduct', () => (!modalitiesIsEmpty() ? Yup.string().required() : Yup.string())),
      performanceModality: Yup.string().when('brokerProduct', () => (showPerformanceModality ? Yup.string().required() : Yup.string())),
      tomador: Yup.string().when('brokerProduct', () => (
        shouldLoadTomador
          ? Yup.string().required()
          : Yup.string()
      )),
    }),
    onSubmit: () => {
      handlePriceQuotation()
    },
  })

  useEffect(() => {
    setTomadorSelected('')
    formik.setFieldValue('tomador', '', false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct, selectedModality])

  useEffect(() => {
    setMultiQuoteProducts([])

    if (!tomadorSelected || isExecutiveRequest) return

    const clientDocument = findByText(tomadorSelected)?.id

    if (!clientDocument) return

    quotationClient().getInsuranceProductsMulticalculo(clientDocument)
      .then((response) => setMultiQuoteProducts(response?.data?.Produtos || []))
      .catch(() => setMultiQuoteProducts([]))
  }, [tomadorSelected, isExecutiveRequest, findByText, quotationClient])

  useEffect(() => {
    if (hasUniqueClient && !hasNoneClient) {
      const onlyOption = findFirst()
      if (onlyOption?.text) {
        setTomadorSelected(onlyOption.text)
        formik.setFieldValue('tomador', onlyOption.text, false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUniqueClient, hasNoneClient, policiesHolder, selectedProduct, selectedModality])

  useEffect(() => {
    if (isAIQuotation && products.length > 0 && !aiFieldsInitialized) {
      const garantiaProduct = products.find((p) => p.path === 'garantia')

      if (garantiaProduct) {
        setSelectedProduct(garantiaProduct)
        formik.setFieldValue('brokerProduct', garantiaProduct.value, false)
        setAiFieldsInitialized(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAIQuotation, products, aiFieldsInitialized])

  useEffect(() => {
    if (isAIQuotation && selectedProduct?.path === 'garantia' && modalities.length > 0 && aiFieldsInitialized) {
      const performanceModalityOption = modalities.find((m) => m.path === 'performance')

      if (performanceModalityOption && (!selectedModality || selectedModality?.path !== 'performance')) {
        setSelectedModality(performanceModalityOption)
        formik.setFieldValue('brokerModality', performanceModalityOption.value, false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAIQuotation, selectedProduct, modalities, aiFieldsInitialized])

  useEffect(() => {
    if (isAIQuotation && showPerformanceModality && !performanceModality) {
      const publicValue = 'Construção, Fornecimento ou Prestação de serviço – Pública'
      setPerformanceModality(publicValue)
      formik.setFieldValue('performanceModality', publicValue, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAIQuotation, showPerformanceModality])

  useEffect(() => {
    if (selectedProduct) {
      const productModalities = productQuotationClient().getModalitiesByProduct(selectedProduct).sort()
      const modalitiesWithIcon = productModalities.map((modality) => ({
        ...modality,
        hasAutomaticEmission: checkIfHasAutomaticEmission(selectedProduct, modality),
      }))

      setModalities(modalitiesWithIcon)
    }

    const showPublicPrivate = selectedProduct && selectedModality && selectedProduct.path === 'garantia' && selectedModality.path === 'performance'
    setShowPerformanceModality(showPublicPrivate)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productQuotationClient, selectedProduct, selectedModality, isAIQuotation])

  useEffect(() => {
    if (!isAIQuotation && aiFieldsInitialized) {
      setSelectedProduct('')
      setSelectedModality('')
      setPerformanceModality('')
      setShowPerformanceModality(false)
      setAiFieldsInitialized(false)
      setModalities([''])
      formik.resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAIQuotation])

  const filterSelectedOptionObject = (e, arrayOptions) => {
    const selectedOptionObject = e.target.value
    if (!selectedOptionObject) {
      return ''
    }

    return arrayOptions.find((option) => option.value === selectedOptionObject) ?? undefined
  }

  const modalityLabel = 'Modalidade'
  const productLabel = isAIQuotation ? 'Produtos com IA' : 'Produto'

  const handleViewAllProducts = () => {
    history.replace('/cotacao', { isAIQuotation: false })
  }

  const noOptionsMessage = (
    <Typography className={classes.noOptionsLabel}>
      Nenhum tomador encontrado
    </Typography>
  )

  return (
    <>
      <Box className={classes.root}>
        <Box display="flex" justifyContent="flex-end" mb={3}>
          {routeSolicitation !== QUOTE_URL_EXECUTIVE_REQUEST && (
            <HelpLink text="Como realizar nova solicitação de Apólice" link={HELP_LINKS.createPolicy} minWidth={350} />
          )}
        </Box>

        <Paper elevation={1}>
          <Grid container>
            <Grid
              item
              xs={12}
              className={classes.header}
            >
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography gutterBottom variant="h5" color="initial">
                    Dados do Produto
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography className={classes.labelInfo}>
                    Informe abaixo os dados do produto
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} className={classes.content}>
              <Grid container spacing={5}>
                <Grid item sm={5} xs={12}>
                  <FormControl fullWidth>
                    <label htmlFor="productLabel" className={classes.label}>
                      {productLabel}
                      {isAIQuotation && (
                      <Typography
                        component="span"
                        variant="body2"
                        style={{
                          color: '#6E7191',
                          fontSize: '14px',
                          marginLeft: '8px',
                          fontWeight: 400,
                        }}
                      >
                        - Clique
                        {' '}
                        <button
                          type="button"
                          onClick={handleViewAllProducts}
                          style={{
                            color: '#1976d2',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontWeight: 600,
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                          }}
                        >
                          aqui
                        </button>
                        {' '}
                        para visualizar todos os produtos
                      </Typography>
                      )}
                    </label>
                    <DropdownSelectOne
                      id="brokerProduct"
                      options={products}
                      {...formik.getFieldProps('brokerProduct')}
                      error={formik.touched.brokerProduct && !!formik.errors.brokerProduct}
                      onClick={(e) => {
                        const optionSelected = filterSelectedOptionObject(e, products)

                        if (!optionSelected) return

                        setSelectedProduct(optionSelected)
                        setSelectedModality('')
                        setModalities([''])
                        setPerformanceModality('')
                        setTomadorSelected('')
                        formik.resetForm({
                          values: { ...DEFAULT_QUOTATION, brokerProduct: optionSelected?.value ?? '' },
                        })
                      }}
                    />
                    <FormHelperText
                      hidden={!formik.touched.brokerProduct || !formik.errors.brokerProduct}
                      error={formik.touched.brokerProduct && !!formik.errors.brokerProduct}
                    >
                      {formik.errors.brokerProduct}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item sm={4} xs={12} hidden={modalitiesIsEmpty()}>
                  <FormControl fullWidth>
                    <label htmlFor="modalityLabel" className={classes.label}>{modalityLabel}</label>
                    <DropdownSelectOne
                      id="brokerModality"
                      options={modalities}
                      placeholder="Produto sem modalidade"
                      {...formik.getFieldProps('brokerModality')}
                      disabled={modalitiesIsEmpty()}
                      error={
                        !modalitiesIsEmpty()
                        && formik.touched.brokerModality
                        && !!formik.errors.brokerModality
                      }
                      renderIcon={(option) => (
                        option.hasAutomaticEmission && (
                        <AutomaticTooltip text="Passível de Emissão Automática">
                          <Automatic style={{ width: 18, height: 18 }} />
                        </AutomaticTooltip>
                        )
                      )}
                      onClick={(e) => {
                        const optionSelected = filterSelectedOptionObject(e, modalities)

                        setSelectedModality(optionSelected)
                        formik.values.brokerModality = optionSelected?.path
                      }}
                    />
                    <FormHelperText
                      hidden={
                        modalitiesIsEmpty()
                        || !formik.touched.brokerModality
                        || !formik.errors.brokerModality
                      }
                      error={formik.touched.brokerModality && !!formik.errors.brokerModality}
                    >
                      {formik.errors.brokerModality}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                {showPerformanceModality && (
                  <Grid item sm={3} xs={12}>
                    <FormControl fullWidth error={formik.touched.performanceModality && !!formik.errors.performanceModality}>
                      <Box className={classes.radioGroup}>
                        <RadioGroup
                          row
                          id="performanceModality"
                          {...formik.getFieldProps('performanceModality')}
                          value={performanceModality}
                          onChange={(e) => {
                            setPerformanceModality(e.target.value)
                            formik.setFieldValue('performanceModality', e.target.value)
                          }}
                        >
                          <FormControlLabel
                            value="Construção, Fornecimento ou Prestação de serviço – Pública"
                            control={<Radio color="primary" />}
                            label="Pública"
                            className={classes.radioLabel}
                          />
                          <FormControlLabel
                            value="Construção, Fornecimento ou Prestação de serviço – Privada"
                            control={<Radio color="primary" />}
                            label="Privada"
                            className={classes.radioLabel}
                          />
                        </RadioGroup>
                      </Box>
                      <FormHelperText>
                        {formik.touched.performanceModality && formik.errors.performanceModality}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}

                {shouldLoadTomador && (
                <Grid item sm={5} xs={12}>
                  <FormControl fullWidth>
                    <label htmlFor="tomadorLabel" className={classes.label}>{tomadorLabel}</label>
                    <Autocomplete
                      id="tomador"
                      color="secondary"
                      noOptionsText={noOptionsMessage}
                      placeholder={`Nome do ${tomadorLabel}`}
                      options={policyHolderOptions}
                      disabled={hasUniqueClient || hasNoneClient}
                      getOptionLabel={(option) => option ?? ''}
                      value={tomadorSelected || null}
                      onInputChange={(e, value) => {
                        if (e?.type === 'click') {
                          setTomadorSelected(value)
                          formik.setFieldValue('tomador', value)
                        }
                      }}
                      getOptionSelected={(option, value) => option === value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onFocus={(event) => event.target.setAttribute('autocomplete', 'none')}
                          placeholder="Procurar"
                          margin="normal"
                          error={formik.touched.tomador && !!formik.errors.tomador}
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                    <FormHelperText
                      hidden={!formik.touched.tomador || !formik.errors.tomador}
                      error={formik.touched.tomador && !!formik.errors.tomador}
                    >
                      {formik.errors.tomador}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                )}

                <Grid item xs={12}>
                  <Box mt={5} className={classes.buttonsStep}>
                    <Box>
                      <ButtonGradient
                        onClick={formik.submitForm}
                        title="Continuar"
                      >
                        Continuar
                      </ButtonGradient>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </>
  )
}

export default CategoryProduct
